import { Globe, Lock, Link2, Users, AlertTriangle } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Alert, AlertDescription } from "@/components/ui/alert";
import LockedContent from "@/components/LockedContent";
import { useSubscription } from "@/hooks/useSubscription";

interface RunPrivacyControlProps {
  visibility: 'public' | 'private' | 'unlisted' | 'anonymous';
  onChange: (visibility: 'public' | 'private' | 'unlisted') => void;
  disabled?: boolean;
}

const RunPrivacyControl = ({ visibility, onChange, disabled = false }: RunPrivacyControlProps) => {
  const { canAccess } = useSubscription();
  const hasAccess = canAccess('adventurer');
  
  // Map the current visibility to a valid toggle value
  const getCurrentValue = () => {
    if (visibility === 'private') return 'private';
    if (visibility === 'unlisted') return 'unlisted';
    return 'public';
  };

  const handleValueChange = (value: string) => {
    if (value) {
      onChange(value as 'public' | 'private' | 'unlisted');
    }
  };

  const content = (
    <div className="space-y-4">
      <Label className="text-sm font-medium">Run Visibility</Label>
      <ToggleGroup 
        type="single" 
        value={getCurrentValue()}
        onValueChange={handleValueChange}
        disabled={disabled || !hasAccess}
        className="grid grid-cols-3 gap-2"
      >
        <ToggleGroupItem 
          value="public" 
          className="flex flex-col items-center gap-1 px-3 py-3 h-auto data-[state=on]:bg-primary/10 data-[state=on]:border-primary border rounded-lg"
        >
          <Globe className="h-4 w-4" />
          <div className="text-center">
            <div className="font-medium text-sm">Public</div>
            <div className="text-xs text-muted-foreground">Anyone, shown in feeds</div>
          </div>
        </ToggleGroupItem>
        <ToggleGroupItem 
          value="unlisted" 
          className="flex flex-col items-center gap-1 px-3 py-3 h-auto data-[state=on]:bg-blue-500/10 data-[state=on]:border-blue-500 border rounded-lg"
        >
          <Link2 className="h-4 w-4" />
          <div className="text-center">
            <div className="font-medium text-sm">Link Only</div>
            <div className="text-xs text-muted-foreground">Only via shared link</div>
          </div>
        </ToggleGroupItem>
        <ToggleGroupItem 
          value="private" 
          className="flex flex-col items-center gap-1 px-3 py-3 h-auto data-[state=on]:bg-amber-500/10 data-[state=on]:border-amber-500 border rounded-lg"
        >
          <Lock className="h-4 w-4" />
          <div className="text-center">
            <div className="font-medium text-sm">Private</div>
            <div className="text-xs text-muted-foreground">Only you can see</div>
          </div>
        </ToggleGroupItem>
      </ToggleGroup>
      
      {/* Location Privacy Disclaimer - shown for non-private visibility */}
      {(visibility === 'public' || visibility === 'unlisted' || visibility === 'anonymous') && (
        <Alert className="bg-amber-500/10 border-amber-500/30">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <AlertDescription className="text-xs text-muted-foreground ml-2">
            <strong className="text-foreground">Privacy Notice:</strong> Your GPS track will be visible to others. 
            Please review your track to ensure it doesn't reveal personal locations (home, work, etc.) 
            that you wish to keep private.
          </AlertDescription>
        </Alert>
      )}
      
      {/* Coming Soon: Club Sharing */}
      <div className="pt-4 border-t border-border">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-sm font-medium flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            Share with Clubs
          </Label>
          <Badge variant="secondary" className="text-xs">Coming Soon</Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          Share this run with your clubs in addition to the visibility setting above. 
          Club members will have access regardless of your visibility choice.
        </p>
      </div>
    </div>
  );

  if (!hasAccess) {
    return (
      <LockedContent
        requiredTier="adventurer"
        featureDescription="control run privacy"
        blurContent={false}
      >
        {content}
      </LockedContent>
    );
  }

  return content;
};

export default RunPrivacyControl;
